import React, { useMemo } from "react";

const isBlank = (v) => (v ?? "").toString().trim().length === 0;

const buildBg = (style, defaultClass) => {
  const mode = style?.bgMode || "default";

  if (mode === "solid" && style?.bgColor) {
    return { style: { backgroundColor: style.bgColor }, className: "" };
  }

  if (mode === "gradient") {
    const from = (style?.bgGradientFrom || "").trim();
    const via = (style?.bgGradientVia || "").trim();
    const to = (style?.bgGradientTo || "").trim();
    const angleNum = Number(style?.bgGradientAngle);
    const angle = Number.isFinite(angleNum) ? angleNum : 180;

    if (from && to) {
      const stops = via ? `${from}, ${via}, ${to}` : `${from}, ${to}`;
      return {
        style: { backgroundImage: `linear-gradient(${angle}deg, ${stops})` },
        className: "",
      };
    }
  }

  return { style: undefined, className: defaultClass };
};

const getAlignValue = (style) => {
  const v = String(style?.textAlign || style?.alignment || "").toLowerCase().trim();
  if (v === "left" || v === "center" || v === "right") return v;
  return "center";
};

const getAlignClass = (style) => {
  const a = getAlignValue(style);
  if (a === "left") return "text-left";
  if (a === "right") return "text-right";
  return "text-center";
};

const getJustifyClass = (style) => {
  const a = getAlignValue(style);
  if (a === "left") return "justify-start";
  if (a === "right") return "justify-end";
  return "justify-center";
};

const parseItems = (config) => {
  if (Array.isArray(config?.items)) return config.items;

  const raw = config?.itemsJson;
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
};

const openCatalog = (catalogId, baseUrl) => {
  const id = (catalogId || "").trim();
  if (!id) return;
  const base = (baseUrl || "https://smartcatalog.shop").replace(/\/+$/, "");
  window.open(`${base}/${encodeURIComponent(id)}`, "_blank", "noopener,noreferrer");
};

export default function CategorySection1({ config, style }) {
  const bg = buildBg(style, "bg-gradient-to-b from-white to-gray-50");
  const alignClass = getAlignClass(style);
  const justifyClass = getJustifyClass(style);

  const items = useMemo(() => parseItems(config), [config]);
  const maxItems = Number.isFinite(Number(config?.maxItems)) ? Number(config.maxItems) : 6;
  const shown = items.slice(0, Math.max(1, maxItems));

  const buttonText = config?.buttonText ?? "Open Full Catalog";
  const baseUrl = config?.baseUrl || "https://smartcatalog.shop";

  const btnStyle = {
    backgroundColor: style?.primaryBtnBg || undefined,
    color: style?.primaryBtnText || undefined,
  };

  return (
    <section
      dir={config?.direction || "ltr"}
      style={bg.style}
      className={`py-24 px-4 ${bg.className}`}
    >
      <div className="container mx-auto max-w-7xl">
        <div className={`${alignClass} mb-14`}>
          <h2
            style={{ color: style?.titleColor || undefined }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-5"
          >
            {config?.title}
          </h2>
          <p
            style={{ color: style?.subtitleColor || undefined }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {config?.subtitle}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {shown.map((it, idx) => (
            <button
              key={it.id || idx}
              type="button"
              onClick={() => openCatalog(config?.catalogId, baseUrl)}
              style={{
                backgroundColor: style?.categoryCardBg || undefined,
                borderColor: style?.categoryCardBorder || undefined,
              }}
              className={`group ${alignClass} rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border`}
            >
              <div className="relative h-56 bg-gray-100 overflow-hidden">
                {it.image ? (
                  <img
                    src={it.image}
                    alt={it.title || "item"}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200" />
                )}

                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{
                    background:
                      style?.categoryImageOverlayColor ||
                      "linear-gradient(to top, rgba(0,0,0,0.40), transparent)",
                  }}
                />
              </div>

              <div className="p-6">
                <div
                  style={{ color: style?.categoryCardTitleColor || undefined }}
                  className="text-lg font-bold text-gray-900 transition-colors"
                >
                  {it.title || "Untitled item"}
                </div>

                {!isBlank(it.subtitle) && (
                  <div
                    style={{ color: style?.categoryCardSubtitleColor || undefined }}
                    className="text-sm text-gray-600 mt-2 line-clamp-2"
                  >
                    {it.subtitle}
                  </div>
                )}

                <div
                  style={{ color: style?.categoryLinkColor || undefined }}
                  className={`mt-5 font-semibold text-sm flex items-center ${justifyClass}`}
                >
                  {config?.viewInCatalogText || "View in catalog"}
                  <span className="ms-2 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform">
                    →
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className={`${alignClass} mt-12`}>
          {!isBlank(config?.catalogId) && !isBlank(buttonText) && (
            <button
              type="button"
              style={style?.primaryBtnBg ? btnStyle : undefined}
              onClick={() => openCatalog(config?.catalogId, baseUrl)}
              className={`inline-flex items-center justify-center px-8 py-4 rounded-2xl font-bold text-lg transition-all duration-300 hover:scale-105 ${
                style?.primaryBtnBg
                  ? ""
                  : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-xl"
              }`}
            >
              {buttonText}
            </button>
          )}

          {!isBlank(config?.hintText) && (
            <div
              style={{ color: style?.categoryCardSubtitleColor || undefined }}
              className="text-sm text-gray-500 mt-4"
            >
              {config.hintText}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}